import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import dayjs from 'dayjs';
import ReminderService from '../../services/ReminderService';
import AccountCell from '../Reminders/AccountCell';
import { getCreatorAccountCellProps } from '../Reminders/accountCellHelper';

interface ResponsibleChangeReminderRecord {
  id?: number | string;
  reminderDate?: string;
  account?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    phone?: string;
    id?: number | string;
  };
  accountId?: number | string;
  content?: string;
  properties?: Record<string, any> | string | null;
}

interface ResponsibleChangeRemindersProps {
  entityId?: number | string | null;
  entityName?: string;
  mode?: 'admin' | 'user';
  active?: boolean;
  refreshKey?: number | string;
}

const parseProperties = (value?: Record<string, any> | string | null) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('ResponsibleChangeReminders property parsing failed:', error);
      return {};
    }
  }
  if (typeof value === 'object') {
    return value;
  }
  return {};
};

const getNameFromValue = (value: any) => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    return value.trim() || undefined;
  }
  if (typeof value === 'object') {
    const firstLast = `${value.firstName ?? ''} ${value.lastName ?? ''}`.trim();
    if (firstLast) return firstLast;
    if (value.fullName) return value.fullName;
    if (value.name) return value.name;
    if (value.email) return value.email;
  }
  return undefined;
};

const collectAssigneeNames = (props: Record<string, any>) => {
  const names = new Set<string>();
  Object.entries(props).forEach(([key, value]) => {
    if (!value) return;
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.includes('assignee') || normalizedKey.includes('responsible')) {
      const extracted = getNameFromValue(value);
      if (extracted) {
        names.add(extracted);
        return;
      }
    }
    if (normalizedKey.includes('user') || normalizedKey.includes('account')) {
      const extracted = getNameFromValue(value);
      if (extracted) {
        names.add(extracted);
      }
    }
  });
  return Array.from(names);
};

const getResponsibleLabel = (record: ResponsibleChangeReminderRecord) => {
  const props = parseProperties(record.properties);
  const names = collectAssigneeNames(props);
  if (names.length > 0) {
    return names.join(', ');
  }
  return record.content || '-';
};

const ResponsibleChangeReminders: React.FC<ResponsibleChangeRemindersProps> = ({
  entityId,
  entityName = 'proposal',
  mode = 'admin',
  active = true,
  refreshKey = 0,
}) => {
  const [data, setData] = useState<ResponsibleChangeReminderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchReminders = async () => {
    if (!entityId) {
      setData([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const params = {
        entityNames: entityName.toString().toUpperCase(),
        entityId: String(entityId),
        page: page - 1,
        size: pageSize,
      };
      const serviceCall =
        mode === 'admin'
          ? ReminderService.getAdminResponsibleChangeReminders
          : ReminderService.getMyResponsibleChangeReminders;
      const response: any = await serviceCall(params);
      const payload = response?.data ?? response ?? {};
      const list: any[] = Array.isArray(payload?.content)
        ? payload.content
        : Array.isArray(payload)
          ? payload
          : [];
      setData(list);
      const nextTotal =
        typeof payload?.totalElements === 'number'
          ? payload.totalElements
          : typeof payload?.total === 'number'
            ? payload.total
            : list.length;
      setTotal(nextTotal);
    } catch (error) {
      console.error('Sorumlu değişikliği hatırlatmaları yüklenemedi:', error);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    fetchReminders();
  }, [active, entityId, entityName, mode, page, pageSize, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [entityId, entityName]);

  const handlePageChange = (nextPage: number, nextSize?: number) => {
    setPage(nextPage);
    if (nextSize && nextSize !== pageSize) {
      setPageSize(nextSize);
    }
  };

  const columns = [
    {
      title: 'Sorumlu',
      key: 'responsible',
      render: (_: any, record: ResponsibleChangeReminderRecord) => getResponsibleLabel(record),
    },
    {
      title: 'Kullanıcı',
      key: 'account',
      render: (_: any, record: ResponsibleChangeReminderRecord) => {
        return <AccountCell {...getCreatorAccountCellProps(record)} />;
      },
    },
    {
      title: 'Tarih',
      dataIndex: 'reminderDate',
      key: 'reminderDate',
      render: (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
  ];

  return (
    <Table
      loading={loading}
      dataSource={data}
      columns={columns as any}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        onChange: handlePageChange,
      }}
      rowKey={(record) => record.id ?? `${record.reminderDate}`}
      locale={{ emptyText: 'Sorumlu değişikliği bulunamadı.' }}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default ResponsibleChangeReminders;
